import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, TrendingUp, UserPlus } from 'lucide-react';
import api from '../api';

const ICONS = {
  payment_failure: AlertTriangle,
  usage_warning: TrendingUp,
  new_client: UserPlus,
};

const COLORS = {
  high: 'text-accent-red',
  medium: 'text-accent-yellow',
  low: 'text-accent-blue',
};

/**
 * AdminNotificationBell
 *
 * Polls GET /api/admin/notifications every 60s, shows a red badge with count,
 * and renders a dropdown panel when clicked. Clicking an item navigates to
 * the associated company page (if company_id is present).
 */
export default function AdminNotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [seenKey, setSeenKey] = useState(() => localStorage.getItem('adminNotifSeenKey') || '');
  const wrapperRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get('/api/admin/notifications');
      setNotifications(data.notifications || []);
    } catch {
      // silent — don't spam errors on every poll
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Track per-notification "seen" set in localStorage for accurate unread count
  const [seenIds, setSeenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('adminNotifSeenIds') || '[]')); }
    catch { return new Set(); }
  });

  const notifKey = (n) => `${n.type}|${n.company_id || ''}|${n.created_at || ''}`;
  const latestKey = notifications[0]?.created_at + '|' + notifications.length;
  const unreadCount = notifications.filter(n => !seenIds.has(notifKey(n))).length;
  const highSeverityUnread = notifications.filter(
    n => !seenIds.has(notifKey(n)) && (n.severity === 'high' || n.severity === 'medium')
  ).length;

  const handleToggle = () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen && latestKey) {
      setSeenKey(latestKey);
      localStorage.setItem('adminNotifSeenKey', latestKey);
      // Mark all currently visible as seen
      const newSeen = new Set(seenIds);
      notifications.forEach(n => newSeen.add(notifKey(n)));
      setSeenIds(newSeen);
      // Cap at 200 to avoid unbounded growth
      const arr = Array.from(newSeen).slice(-200);
      localStorage.setItem('adminNotifSeenIds', JSON.stringify(arr));
    }
  };

  const handleClick = (n) => {
    setOpen(false);
    if (n.company_id) navigate(`/admin/companies/${n.company_id}`);
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'nå';
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}t`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid="admin-notification-bell">
      <button
        type="button"
        onClick={handleToggle}
        className="relative p-2 text-slate-400 hover:text-white rounded-lg hover:bg-navy-800 transition-colors"
        aria-label="Varsler"
      >
        <Bell size={18} className={highSeverityUnread > 0 ? 'animate-pulse text-accent-red' : ''} />
        {unreadCount > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 ${highSeverityUnread > 0 ? 'bg-accent-red' : 'bg-accent-blue'} text-white text-[10px] font-bold rounded-full flex items-center justify-center`}
            data-testid="admin-notif-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute bottom-full right-0 mb-2 w-80 max-h-[70vh] overflow-y-auto bg-navy-800 border border-navy-600 rounded-lg shadow-2xl z-50"
          data-testid="admin-notif-dropdown"
        >
          <div className="px-4 py-3 border-b border-navy-600 flex items-center justify-between">
            <span className="font-heading text-sm font-semibold text-white">Varsler</span>
            <span className="text-xs text-slate-500">{notifications.length}</span>
          </div>
          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">Ingen varsler</div>
          ) : (
            <div className="divide-y divide-navy-600/60">
              {notifications.map((n, i) => {
                const Icon = ICONS[n.type] || Bell;
                const color = COLORS[n.severity] || 'text-slate-400';
                return (
                  <button
                    key={`${n.type}-${n.company_id}-${i}`}
                    type="button"
                    onClick={() => handleClick(n)}
                    className="w-full text-left px-4 py-3 hover:bg-navy-700 transition-colors flex gap-3 items-start"
                  >
                    <Icon size={16} className={`${color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{n.message}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{formatTime(n.created_at)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
